import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Camera, Upload, Loader2, FileText, X, Check, Eye,
  PenTool, Sparkles, AlertCircle, ImageIcon
} from 'lucide-react';

const HandwrittenUpload = ({ onDocumentCreated, hasSubscription }) => {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | processing | review | confirmed
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [editedText, setEditedText] = useState('');
  const [title, setTitle] = useState('Handwritten Notes');
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList).filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(ext);
    });

    if (newFiles.length === 0) {
      toast.error('Please select JPG, PNG, or WebP images only');
      return;
    }

    const total = images.length + newFiles.length;
    if (total > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }

    const newPreviews = newFiles.map(f => URL.createObjectURL(f));
    setImages(prev => [...prev, ...newFiles]);
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(previews[index]);
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const uploadAndProcess = async () => {
    if (images.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    setUploading(true);
    try {
      // Step 1: Upload images
      const formData = new FormData();
      images.forEach(img => formData.append('files', img));

      const uploadRes = await axios.post(`${API}/study-builder/upload-images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const docId = uploadRes.data.id;
      setDocumentId(docId);
      toast.success(`${uploadRes.data.image_count} image(s) uploaded successfully`);

      // Step 2: Process OCR
      setUploading(false);
      setProcessing(true);
      setStep('processing');

      const ocrRes = await axios.post(`${API}/study-builder/ocr-process/${docId}`);
      const text = ocrRes.data.extracted_text || '';
      setExtractedText(text);
      setEditedText(text);
      setStep('review');
      toast.success('Text extracted successfully! Review and edit if needed.');

    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to process images');
      if (step === 'processing') setStep('upload');
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  };

  const confirmText = async () => {
    if (!editedText || editedText.trim().length < 20) {
      toast.error('Text must be at least 20 characters');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('text', editedText);
      formData.append('title', title);

      await axios.post(`${API}/study-builder/confirm-text/${documentId}`, formData);
      setStep('confirmed');
      toast.success('Notes confirmed! You can now generate your Study Dashboard.');
      if (onDocumentCreated) onDocumentCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm text');
    }
  };

  const resetFlow = () => {
    previews.forEach(p => URL.revokeObjectURL(p));
    setStep('upload');
    setImages([]);
    setPreviews([]);
    setDocumentId(null);
    setExtractedText('');
    setEditedText('');
    setTitle('Handwritten Notes');
  };

  // Step: Upload images
  if (step === 'upload') {
    return (
      <div className="space-y-4">
        <div
          data-testid="handwritten-dropzone"
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
            dragActive ? 'border-orange-500 bg-orange-50' : 'border-gray-300 bg-gray-50 hover:border-orange-400 hover:bg-orange-50/50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            data-testid="handwritten-file-input"
            type="file"
            className="hidden"
            accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
            multiple
            onChange={(e) => e.target.files?.length && handleFiles(e.target.files)}
          />
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-3">
              <Camera className="w-7 h-7 text-orange-600" />
            </div>
            <p className="text-base font-medium text-gray-700 mb-1">
              Upload photos of your handwritten notes
            </p>
            <p className="text-sm text-gray-500 mb-2">Drag & drop or click to browse</p>
            <p className="text-xs text-gray-400">JPG, PNG, WebP, or HEIC — up to 10 images, 20MB each</p>
          </div>
        </div>

        {/* Image Previews */}
        {previews.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">
                {images.length} image{images.length !== 1 ? 's' : ''} selected
              </p>
              <Button
                data-testid="clear-images-btn"
                variant="ghost"
                size="sm"
                onClick={resetFlow}
                className="text-gray-500 hover:text-red-500"
              >
                Clear all
              </Button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {previews.map((src, i) => (
                <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                  <img src={src} alt={`Note ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    data-testid={`remove-image-${i}`}
                    onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-1 py-0.5 truncate">
                    {images[i]?.name}
                  </div>
                </div>
              ))}
            </div>
            <Button
              data-testid="process-ocr-btn"
              onClick={uploadAndProcess}
              disabled={uploading}
              className="w-full rounded-xl bg-orange-600 hover:bg-orange-700"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><PenTool className="w-4 h-4 mr-2" /> Extract Text from {images.length} Image{images.length !== 1 ? 's' : ''}</>
              )}
            </Button>
          </div>
        )}

        <p className="text-xs text-gray-400 text-center">
          Upload is free. AI text extraction is free. You only pay when generating the Study Dashboard.
        </p>
      </div>
    );
  }

  // Step: Processing OCR
  if (step === 'processing') {
    return (
      <div className="flex flex-col items-center py-12 space-y-4" data-testid="ocr-processing">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800">Reading Your Handwriting...</h3>
        <p className="text-sm text-gray-500 text-center max-w-md">
          AI is analyzing your images and extracting text. This may take a moment depending on the number of pages.
        </p>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Sparkles className="w-3 h-3" />
          <span>Powered by GPT-4o Vision</span>
        </div>
      </div>
    );
  }

  // Step: Review extracted text
  if (step === 'review') {
    return (
      <div className="space-y-4" data-testid="ocr-review">
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            Review the extracted text below. Edit any mistakes before confirming.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <Input
            data-testid="ocr-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Biology Chapter 5 Notes"
            className="rounded-xl"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">Extracted Text</label>
            <span className="text-xs text-gray-400">{editedText.length} characters</span>
          </div>
          <Textarea
            data-testid="ocr-text-editor"
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            rows={14}
            className="rounded-xl font-mono text-sm"
            placeholder="Extracted text will appear here..."
          />
        </div>

        <div className="flex gap-3">
          <Button
            data-testid="ocr-back-btn"
            variant="outline"
            onClick={resetFlow}
            className="rounded-xl"
          >
            Start Over
          </Button>
          <Button
            data-testid="ocr-confirm-btn"
            onClick={confirmText}
            className="flex-1 rounded-xl bg-orange-600 hover:bg-orange-700"
          >
            <Check className="w-4 h-4 mr-2" /> Confirm & Save Notes
          </Button>
        </div>
      </div>
    );
  }

  // Step: Confirmed
  if (step === 'confirmed') {
    return (
      <div className="flex flex-col items-center py-8 space-y-4" data-testid="ocr-confirmed">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
          <Check className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800">Notes Saved!</h3>
        <p className="text-sm text-gray-500 text-center max-w-md">
          Your handwritten notes have been digitized. You can now generate a Study Dashboard from them below.
        </p>
        <Button
          data-testid="ocr-upload-more-btn"
          variant="outline"
          onClick={resetFlow}
          className="rounded-xl"
        >
          <Camera className="w-4 h-4 mr-2" /> Scan More Notes
        </Button>
      </div>
    );
  }

  return null;
};

export default HandwrittenUpload;
